import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Sparkles } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // 2026-06-04: Sun Med Portal removed from main public nav; now lives in Footer → Quick Links.
  // 2026-06-10: Added Hey Taxi! (blog) before Contact.
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Bookings', path: '/book' },
    { name: 'Flight Info', path: '/flights' },
    { name: 'Fare Calculator', path: '/fare-calculator' },
    { name: 'Hey Taxi!', path: '/hey-taxi' },
    { name: 'Contact', path: '/#contact' }
  ];

  const isActive = (path) => {
    if (path === '/#contact') return false;
    // Hey Taxi nav item should also light up on individual post pages.
    if (path === '/hey-taxi') {
      return location.pathname === '/hey-taxi' || location.pathname.startsWith('/hey-taxi/');
    }
    return location.pathname === path;
  };

  const premierActive = location.pathname === '/premier';

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md z-50 border-b border-cyan-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo and Brand */}
          <Link to="/" className="flex items-center space-x-3 group" data-testid="navbar-logo-link">
            <img 
              src="/images/taxi98-logo.png" 
              alt="Taxi-98 Bahamas"
              className="h-20 w-auto transform group-hover:scale-105 transition-transform duration-300"
            />
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-gray-900 group-hover:text-cyan-600 transition-colors">
                Taxi-98
              </span>
              <span className="text-xs text-gray-600 font-medium">
                Nassau Airport Taxi
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6" data-testid="navbar-desktop-links">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`navbar-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-base font-medium transition-all duration-300 hover:text-cyan-600 ${
                  isActive(link.path) ? 'text-cyan-600' : 'text-gray-700'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Premier — small premium pill, sits before Call Now */}
            <Link
              to="/premier"
              data-testid="navbar-link-premier"
              aria-current={premierActive ? 'page' : undefined}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-semibold tracking-wide border transition-all duration-300 ${
                premierActive
                  ? 'bg-gray-900 text-amber-200 border-amber-300/60 shadow-sm'
                  : 'bg-gray-900 text-amber-200 border-amber-300/40 hover:border-amber-300/80 hover:text-amber-100 hover:shadow-md'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              <span>Premier</span>
            </Link>
          </div>

          {/* Call Button */}
          <div className="hidden md:flex items-center">
            <a
              href="tel:+12428107335"
              data-testid="navbar-call-now-btn"
              className="flex items-center space-x-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-lg font-bold hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/30"
            >
              <Phone className="w-4 h-4" />
              <span>Call Now</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            data-testid="navbar-mobile-menu-toggle"
            className="md:hidden text-gray-700 hover:text-cyan-600 transition-colors duration-300"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 space-y-3 border-t border-cyan-100" data-testid="navbar-mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsOpen(false)}
                data-testid={`navbar-mobile-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`block px-4 py-2 text-base font-medium transition-colors duration-300 rounded-lg ${
                  isActive(link.path)
                    ? 'bg-cyan-50 text-cyan-600'
                    : 'text-gray-700 hover:bg-cyan-50 hover:text-cyan-600'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Premier — mobile */}
            <Link
              to="/premier"
              onClick={() => setIsOpen(false)}
              data-testid="navbar-mobile-link-premier"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-base font-semibold transition-colors duration-300 ${
                premierActive
                  ? 'bg-gray-900 text-amber-200 border border-amber-300/50'
                  : 'bg-gray-900 text-amber-200 border border-amber-300/30 hover:bg-gray-800'
              }`}
            >
              <Sparkles className="w-4 h-4" aria-hidden="true" />
              <span>Premier</span>
            </Link>

            <a
              href="tel:+12428107335"
              data-testid="navbar-mobile-call-now-btn"
              className="flex items-center space-x-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-3 rounded-lg font-bold justify-center hover:from-cyan-600 hover:to-blue-600 transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              <span>Call Now</span>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
